import Phaser from 'phaser';
import { BIKE } from '../config.js';
import {
  bikeDisplayHeight, bikeWheelOffset, centreYFromGround, groundYFromCentre, perspectiveScale, riderOnParkedBike,
} from './carCatalog.mjs';
import { gameState } from '../state/gameState.js';
import { WalkController } from './WalkController.js';
import { BikeSprite } from './BikeSprite.js';
import { dismountSpot } from './bikeRiderLayout.mjs';
import { bikeArtKeys, ownBikeArtKeep, wornBikeItems } from './bikeArtKeys.mjs';
import { ensureBikeArt, releaseBikeArt } from './bikeArt.js';
import { parkedFacing8, facesLeft } from './dir8.mjs';
import { centreForFeet } from './outdoorScale.mjs';
import { bikeSession, setRiding } from './bikeSession.js';
import { mountNote, staleRide } from './bikeRide.mjs';
import { ownedBikes, bikeKind, resolveRideId } from './ownedBikes.mjs';
import { carriedRide } from './rideMotion.mjs';
import { showActionsModal } from './Modal.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { audioManager } from '../audio/AudioManager.js';
import { virtualInput } from './virtualInput.js';
import { isInputLocked, lockInput, unlockInput } from './inputLock.js';
import { LABEL_DEPTH } from './ySort.js';
import { iconText } from './iconText.js';

// Owns everything bike-related inside a ride-capable scene (MainScene / OutsideScene / BackyardScene):
// the parked bikes (one BikeSprite per owned bike, on its kickstand), the rider (a BikeSprite with the
// player's character, clothes, bike and design; bike plan 3), getting on and off in place, grease-based
// speed + drain (synced to the server), and the spacebar / click interactions. A player owns any number of
// bikes, one of each design (bike plan 6), so a bike is named by its design id (`id`); its kind (`type`,
// 'pink' / 'blue') picks its size, art and speed. Each bike's parked location, direction + grease level
// live on the server (gameState.raw.bikes[id]), so dropping a bike somewhere survives a page reload. The scene keeps its own walking Natalka and only needs to:
//   - pass it in here (so we can hide/show it while riding),
//   - call bike.update(dt) when bike.isRiding, else its own walker,
//   - gate its house/shop exits on !bike.isRiding (with a toast).
export class BikeController {
  // `mountY` / `dismountY` are the outdoor scenes' way of saying where the ground is: the
  // ground line the rider's wheels start on when she climbs aboard, and the sprite-CENTRE y
  // the character lands on when she gets off (the street walks her back up to the pavement
  // when she dismounts in the road). Without them the bike's own ground is used.
  constructor(scene, { natalka, floor = {}, parkedDefaults, onMount = null, onDismount = null, onBroadcast = null, canRide = null, mountY = null, dismountY = null }) {
    this.scene = scene;
    this.rig = scene.cache.json.get('bike_rig');
    this._mountY = mountY;
    this._dismountY = dismountY;
    this._floorBlocked = floor.blocked ?? null;   // (x, feetY) => true inside an obstacle
    // Optional veto from the scene: you can't climb onto a bike while you're sitting in a
    // car (the street runs both controllers side by side and SPACE reaches both).
    this._canRide = canRide ?? (() => true);
    this.natalka = natalka;
    this.sceneKey = scene.scene.key;
    this.parkedDefaults = parkedDefaults;
    this._onMount = onMount;
    this._onDismount = onDismount;
    this._onBroadcast = onBroadcast;
    this._pendingDrain = 0;     // grease burned since last server sync (flushed on dismount)
    this._mountLocks = 0;       // input locks held while the rider's art loads (released on shutdown too)
    this._mounting = false;
    // Outdoors (the street, the yard, the Podwórko) the whole world is drawn at one real-world scale, so
    // the rider is a 1.4 m child on a bike and the parked bike the same art's scale; indoors 240 px.
    this._outdoor = (scene.worldScale ?? 1) !== 1;
    this._perspective = scene.perspective;   // which y-perspective this scene's ground uses

    // Parked bikes (click to mount when near), one per owned bike, made and dropped by refresh() as bikes are
    // bought and sold.
    this.parked = new Map();   // id → BikeSprite

    // Hover label shared by the parked bikes ("whose bike is this?").
    this._parkedTip = iconText(scene, 0, 0, 'Twój rower', {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(LABEL_DEPTH).setVisible(false);

    // The rider: the layered BikeSprite, dressed and painted at mount; moved by its own WalkController,
    // which speeds it up and lets it roll to a stop (inertia) and turns it through setFacing8 / setMotion.
    this.rider = new BikeSprite(scene, 0, 0, { type: this._kind(bikeSession.ridingId), char: this._char(),
                                               items: this._items(), dir8: bikeSession.dir8, visible: false })
      .setLook(window.__myLook ?? null)
      .setDisplayHeight(this._riderBaseH(this._kind(bikeSession.ridingId)))
      .setDepth(1)
      .setInteractive({ useHandCursor: true });
    this.rider.on('pointerup', () => { if (this.isRiding) this.dismount(); });

    this.riderWalker = new WalkController(scene, this.rider, {
      ...floor,
      spriteHeight: this._riderBaseH('pink'),
      speed: () => (bikeSession.greaseLocal > 0
        ? BIKE.TYPES[this._kind(bikeSession.ridingId)].SPEED_GREASED
        : BIKE.SPEED_DRY),
      inertia: true,
      mode: 'bike',
      onBroadcast: (p) => this._onBroadcast?.({ ...p, ...this._rideTag() }),
    });

    // Spacebar: mount when near / dismount.
    this._spaceHandler = () => this._onSpace();
    scene.input.keyboard.on('keydown-SPACE', this._spaceHandler);
    scene.events.once('shutdown', () => this._onShutdown());

    // If we arrived here mid-ride (crossed scenes on the bike), resume riding at the
    // spawn point (no bell); otherwise show whichever owned bikes are parked in this scene.
    const bikes = gameState.raw?.bikes;
    if (bikeSession.riding) {
      // A ride saved before bike plan 6 names the bike's kind: that is the player's one bike of that kind.
      const id = resolveRideId(bikeSession.ridingId, bikes);
      if (id && id !== bikeSession.ridingId) setRiding(true, id);
    }
    if (bikeSession.riding && this._owned(bikeSession.ridingId)) {
      // Resuming a ride (scene change or a page refresh). On a fresh page load the
      // in-memory grease is 0, so reseed it from the server.
      this._enterRiding(bikeSession.ridingId, this.natalka.x,
        this._mountCentreY(bikeSession.ridingId), { reseedGrease: bikeSession.greaseLocal === 0, dir8: bikeSession.dir8, carry: true });
    } else if (staleRide(bikeSession, bikes)) {
      setRiding(false);   // that bike was sold (on another device): clear the flag, or the other bikes stay locked
    }
    this.refresh();
  }

  _char() { return window.__myCharacter ?? 'natalka'; }
  _items() { return this.rig ? wornBikeItems(this.rig, this._char(), gameState.raw?.inventory?.equipped) : []; }
  _design(id) { return this._bikeState(id).design ?? id ?? null; }
  // The kind of the bike `id` ('pink' / 'blue'): what sizes, draws and speeds it.
  _kind(id) { return bikeKind(gameState.raw?.bikes, id) ?? 'pink'; }
  // What a ride broadcast says about the bike, for the peers who draw it.
  _rideTag() { return { bike_type: this._kind(bikeSession.ridingId), bike_design: this._design(bikeSession.ridingId) }; }

  // The two bike sizes, in the units this scene draws in — outdoors, on the perspective's
  // REFERENCE plane (the pavement's walking line). One scale draws both: the parked bike is the rider's
  // canvas scale, so the bike is the same size ridden or parked.
  _riderH() { return this._outdoor ? undefined : BIKE.RIDER_H; }
  _riderBaseH(type) { return bikeDisplayHeight('rider', type, this.rig, this._riderH()); }
  _parkedBaseH(type) { return bikeDisplayHeight('parked', type, this.rig, this._riderH()); }

  // …and the same sizes for a bike whose wheels stand on `groundY`: a bike down at the near
  // kerb is drawn bigger than one up on the pavement (see carCatalog's PERSPECTIVE).
  _scaleAt(groundY) { return this._outdoor ? perspectiveScale(groundY, this._perspective) : 1; }
  _riderHAt(type, groundY) { return this._riderBaseH(type) * this._scaleAt(groundY); }
  _parkedHAt(type, groundY) { return this._parkedBaseH(type) * this._scaleAt(groundY); }

  /** The line the ridden bike's wheels are on right now (outdoors), or the rider's y. */
  _riderGroundY(type = this._kind(bikeSession.ridingId)) {
    if (!this._outdoor) return this.rider.y + this._wheelOffset('rider', type);
    return groundYFromCentre(this._wheelOffset('rider', type), this.rider.y, this._perspective);
  }

  /** How far below the ridden bike's centre its wheels are, at the size it is drawn. */
  get riderWheelOffset() {
    return this._riderGroundY() - this.rider.y;
  }

  // Px from a sprite's centre down to its wheels, on the reference plane: the BikeSprite's box ends on the
  // ground line, so half its height.
  _wheelOffset(kind, type) { return bikeWheelOffset(kind, type, this.rig, this._riderH()); }

  // Tell the room our ride state changed (so peers swap to/from the bike sprite even
  // when we mount/dismount without moving). No-op if the scene isn't broadcasting.
  _broadcastMode(mode, sprite) {
    const dir = mode === 'bike' ? this.rider.facing8 : (sprite.facing8 ?? null);
    this._onBroadcast?.({
      x: sprite.x, y: sprite.y,
      facing: facesLeft(this.rider.facing8) ? 'left' : 'right',
      mode, dir,
      ...(mode === 'bike' ? this._rideTag() : {}),
    });
  }

  get isRiding() { return bikeSession.riding && this.rider.visible; }

  /** Which bike is under us right now (its design id), or null on foot. */
  get ridingId() { return this.isRiding ? bikeSession.ridingId : null; }

  /** The kind of the bike under us ('pink' / 'blue'), or null on foot. */
  get ridingType() { return this.isRiding ? this._kind(bikeSession.ridingId) : null; }

  _owned(id) { return !!gameState.raw?.bikes?.[id]?.owned; }
  _bikeState(id) { return gameState.raw?.bikes?.[id] ?? {}; }
  _parkedHere(id) { return (this._bikeState(id).parked_scene ?? 'MainScene') === this.sceneKey; }
  // Where a parked bike stands: its sprite centre, plus (outdoors) the ground line its
  // wheels are on — which is what sizes and y-sorts it, since a bike can now be left on the
  // road as well as on the pavement.
  //
  // Stored y is the RIDER's centre at dismount; the parked bike is shorter, so line the two up by their
  // WHEELS or the parked bike floats above the pavement it was left on. Scene defaults are parked centres, one
  // per kind.
  _parkedPos(id) {
    const type = this._kind(id);
    const { parked_x: px, parked_y: py } = this._bikeState(id);
    const d = this.parkedDefaults[type];
    const here = this._parkedHere(id);
    const x = (px != null && here) ? px : d.x;
    const stored = (py != null && here);
    if (!this._outdoor) {
      const riderToParked = this._wheelOffset('rider', type) - this._wheelOffset('parked', type);
      return { x, y: stored ? py + riderToParked : d.y, groundY: null };
    }
    // Outdoors the two sizes scale with depth, so the conversion goes through the ground line rather than
    // through a fixed difference of offsets.
    const groundY = groundYFromCentre(
      this._wheelOffset(stored ? 'rider' : 'parked', type), stored ? py : d.y, this._perspective);
    return { x, y: centreYFromGround(this._wheelOffset('parked', type), groundY, this._perspective), groundY };
  }
  _near(id) {
    const { x, y } = this._parkedPos(id);
    return Phaser.Math.Distance.Between(this.natalka.x, this.natalka.y, x, y) <= BIKE.NEAR_DISTANCE;
  }
  // Nearest owned bike parked in this scene within reach, or null.
  _nearestMountable() {
    let best = null, bestD = Infinity;
    for (const { id } of this._parkedBikesHere()) {
      const { x, y } = this._parkedPos(id);
      const d = Phaser.Math.Distance.Between(this.natalka.x, this.natalka.y, x, y);
      if (d <= BIKE.NEAR_DISTANCE && d < bestD) { best = id; bestD = d; }
    }
    return best;
  }

  // The owned bikes parked in this scene.
  _parkedBikesHere() {
    return ownedBikes(gameState.raw?.bikes).filter((b) => this._parkedHere(b.id));
  }

  // Any owned bike parked in this scene (regardless of distance) — distinguishes "too
  // far, walk closer" from "no bike here at all" (silent no-op).
  _anyOwnedParkedHere() {
    return this._parkedBikesHere().length > 0;
  }

  // One parked bike's sprite. Depth 1 so the walking character (bumped above by the scene) always renders in
  // front of it.
  _makeParked(id) {
    const type = this._kind(id);
    const bike = new BikeSprite(this.scene, 0, 0, { type, design: this._design(id), parked: true,
                                                    dir8: parkedFacing8(this._bikeState(id).parked_facing),
                                                    visible: false })
      .setDisplayHeight(this._parkedBaseH(type))
      .setDepth(1)
      .setInteractive({ useHandCursor: true });
    bike.on('pointerup', () => this._onParkedClick(id));
    bike.on('pointerover', () => this._showParkedTip(bike));
    bike.on('pointerout', () => this._parkedTip.setVisible(false));
    return bike;
  }

  // Show/hide each parked bike based on ownership + where it's parked + ride state; a bike bought since gets its
  // sprite here and a sold one loses it. While riding (or climbing on), the bike under us is hidden (it's drawn as
  // `this.rider` instead) but every OTHER owned bike parked in this scene must still be visible — e.g. mount one
  // bike, walk to town: the others parked there stay visible the whole time.
  refresh() {
    const mine = new Set(ownedBikes(gameState.raw?.bikes).map((b) => b.id));
    for (const [id, bike] of this.parked) {
      if (!mine.has(id)) { bike.destroy(); this.parked.delete(id); }
    }
    for (const id of mine) {
      const hide = (bikeSession.riding || this._mounting) && id === bikeSession.ridingId;
      const show = !hide && this._parkedHere(id);
      let bike = this.parked.get(id);
      if (show) {
        if (!bike) { bike = this._makeParked(id); this.parked.set(id, bike); }
        const type = this._kind(id);
        const { x, y, groundY } = this._parkedPos(id);
        // A parked bike keeps the direction it was ridden in, on its kickstand.
        bike.setDesign(type, this._design(id)).setFacing8(parkedFacing8(this._bikeState(id).parked_facing));
        bike.setPosition(x, y).setVisible(true);
        if (this._outdoor) {
          // Drawn at the perspective size of the ground it stands on, and y-sorted by that
          // ground line: a bike left in the near lane is in FRONT of a car in the far one.
          bike.setDisplayHeight(this._parkedHAt(type, groundY)).setDepth(groundY);
        }
      } else {
        bike?.setVisible(false);
      }
    }
    this._parkedTip.setVisible(false);
  }

  _showParkedTip(bike) {
    if (!bike.visible) return;
    this._parkedTip.setPosition(bike.x, bike.y - bike.displayHeight / 2 - 6).setVisible(true);
  }

  _onSpace() {
    if (this.isRiding) { this.dismount(); return; }
    if (!this._canRide()) return;
    this._tryMountFromNear();
  }

  _tryMountFromNear() {
    if (bikeSession.riding || this._mounting) return;
    const id = this._nearestMountable();
    if (id) { this.mount(id); return; }
    if (this._anyOwnedParkedHere()) showToast(this.scene, 'Podejdź bliżej do roweru {bike}');
  }

  async _onParkedClick(id) {
    if (!this._owned(id) || bikeSession.riding || this._mounting || !this._canRide()) return;
    if (!this._near(id)) { showToast(this.scene, 'Podejdź bliżej do roweru {bike}'); return; }

    const tubes = gameState.raw?.inventory?.bike_grease ?? 0;
    const greaseMax = gameState.raw?.catalog?.bike?.grease_max ?? 100;
    const chainFull = (this._bikeState(id).grease ?? 0) >= greaseMax;

    // The grease button is always shown; it's disabled when the chain is already
    // full, or when you have no grease tubes to apply.
    let greaseAction;
    if (chainFull) {
      greaseAction = { label: 'Nasmaruj {wrench} (pełny)', disabled: true };
    } else if (tubes <= 0) {
      greaseAction = { label: 'Nasmaruj {wrench} (brak)', disabled: true };
    } else {
      greaseAction = {
        label: `Nasmaruj {wrench} (${tubes})`,
        fn: async () => {
          try { await gameState.greaseBike(id); showToast(this.scene, 'Nasmarowano łańcuch! {wrench}'); }
          catch (e) { showToast(this.scene, messageFor(e)); }
        },
      };
    }
    const actions = [{ label: 'Wsiądź {bike}', fn: () => this.mount(id) }, greaseAction];
    showActionsModal(this.scene, { title: 'Rowerek {bike}', body: 'Co robimy?', actions });
  }

  // Climbing on: the bell at once, then (the rider's art loaded first, so it never shows a half-drawn bike)
  // the walker gives way to the rider in the stop pose, facing the way the bike was parked, and it can ride
  // at once. No hop (G11, owner 2026-10-24: "completely unnecessary").
  mount(id) {
    if (bikeSession.riding || this._mounting) return;
    audioManager.playSfx('bike_bell');
    this._mounting = true;
    bikeSession.ridingId = id;
    const keys = bikeArtKeys({ rig: this.rig, char: this._char(), type: this._kind(id), design: this._design(id),
                               items: this._items(), look: window.__myLook ?? null });
    this._mountLock();
    ensureBikeArt(this.scene, keys, () => {
      if (!this.scene?.sys?.isActive()) return;
      this._mounting = false;
      const at = this._riderOnBike(id);
      this._enterRiding(id, at.x, at.y, { reseedGrease: true, dir8: parkedFacing8(this._bikeState(id).parked_facing) });
      const note = mountNote(bikeSession.greaseLocal);
      if (note) showToast(this.scene, note);
      this._mountUnlock();
    });
  }

  // Getting on: the rider sits on the parked bike, its wheels where the parked bike's were (dismount stores the
  // rider's centre as the parked spot, so on/off again and again never moves the bike, G11).
  _riderOnBike(id) {
    const type = this._kind(id);
    const p = this._parkedPos(id);
    if (this._outdoor) return { x: p.x, y: riderOnParkedBike(type, p.groundY, this._perspective, this.rig) };
    return { x: p.x, y: p.y - (this._wheelOffset('rider', type) - this._wheelOffset('parked', type)) };
  }

  // Resuming a ride after a scene change puts the bike's WHEELS where the character's feet were — the rider sprite is
  // taller than she is, so simply keeping her centre y would drop the bike into the road.
  _mountCentreY(id) {
    const g = this._mountY?.();
    if (!this._outdoor || !Number.isFinite(g)) return this.natalka.y;
    return centreYFromGround(this._wheelOffset('rider', this._kind(id)), g, this._perspective);
  }

  _enterRiding(id, x, y, { reseedGrease, dir8, carry = false }) {
    const type = this._kind(id);
    setRiding(true, id);
    bikeSession.dir8 = dir8;
    if (reseedGrease) bikeSession.greaseLocal = this._bikeState(id).grease ?? 0;
    const rh = this._riderHAt(type, groundYFromCentre(this._wheelOffset('rider', type), y, this._perspective));
    this.rider.setRider(this._char(), this._items()).setDesign(type, this._design(id));
    this.rider.setDisplayHeight(rh).setFacing8(dir8).setStop();
    this.riderWalker.speed = 0;
    this.riderWalker.setSpriteHeight(rh);
    this.parked.get(id)?.setVisible(false);
    this.natalka.setVisible(false);
    this.rider.setPosition(x, y).setVisible(true);
    // Ridden in from the last scene, it goes on at the speed it had (owner G11); got on, it starts at rest.
    if (carry) this.riderWalker.carryRide(carriedRide({ share: bikeSession.speedShare, dir: bikeSession.rideDir }, this.riderWalker.topSpeed));
    this._broadcastMode('bike', this.rider);
    this._onMount?.(this.rider);
  }

  // Getting off: the bike stops at once and stays where it stood, on its kickstand, facing the way it was
  // ridden; the character stands beside it, on its near-leg side.
  async dismount() {
    if (!bikeSession.riding) return;
    const id = bikeSession.ridingId;
    const dir8 = this.rider.facing8;
    setRiding(false);
    bikeSession.dir8 = dir8;
    bikeSession.speedShare = 0; bikeSession.rideDir = null;
    this.riderWalker.speed = 0;
    this.rider.setRide({ speed: 0, pedalling: false });
    const x = this.rider.x;
    const y = this.rider.y;
    const groundY = this._riderGroundY(this._kind(id));

    // Optimistically record the parked spot locally so refresh() shows it right away.
    const bs = gameState.raw?.bikes?.[id];
    if (bs) {
      bs.parked_scene = this.sceneKey;
      bs.parked_x = x;
      bs.parked_y = y;
      bs.parked_facing = dir8;
    }

    // Beside the bike, on a side free of obstacles and inside the scene; the street clamps her feet into its
    // walking band (the bike stays where it stood).
    const w = this.riderWalker;
    const free = (fx, fy) => fx >= w.boundsXMin && fx <= w.boundsXMax
      && fy <= w.boundsYMax + w._spriteHeight / 2                         // not past the near edge
      && !(w._floorPolyline && fy < w.getFloorYAt(fx))                     // nor behind the far floor line
      && !this._floorBlocked?.(fx, fy);
    const off = dismountSpot(dir8, this.rider.displayHeight, (dx, dy) => free(x + dx, groundY + dy));
    const feetY = groundY + off.dy;
    const ny = this._dismountY?.(feetY) ?? (this._outdoor ? centreForFeet(this.scene, feetY) : this.natalka.y + off.dy);
    this.natalka.setPosition(x + off.dx, ny).setVisible(true);
    this.natalka.setFacing8?.(dir8);
    this.rider.setVisible(false);
    this._broadcastMode('walk', this.natalka);
    this._onDismount?.(this.natalka);
    this.refresh();

    // Persist sequentially with the PARK first: every action returns the full state,
    // so a later grease-sync response carrying the old parked spot would otherwise
    // clobber the new one (teleport). Park last-writes the location, then drain grease.
    try {
      await gameState.parkBike(this.sceneKey, x, id, dir8, y);
      if (this._pendingDrain > 0) {
        const amount = this._pendingDrain;
        this._pendingDrain = 0;
        await gameState.consumeBikeGrease(amount, id);
      }
    } catch (_) { /* offline — local visual already correct */ }
    this.refresh();
  }

  _mountLock() { lockInput(this.scene); this._mountLocks += 1; }
  _mountUnlock() { if (this._mountLocks > 0) { this._mountLocks -= 1; unlockInput(this.scene); } }

  update(deltaMs) {
    if (!this.isRiding) return;
    this.riderWalker.update(deltaMs);
    // Outdoors the bike may be anywhere between the shopfronts and the far grass verge, so
    // its size follows the line its wheels are on, frame by frame.
    if (this._outdoor) {
      const type = this._kind(bikeSession.ridingId);
      const rh = this._riderHAt(type, this._riderGroundY(type));
      this.rider.setDisplayHeight(rh);
      // …and the walker's floor maths needs the same height (its floor lines are the WHEEL
      // line, kept half a sprite below the centre), or a rider who mounted near the viewer
      // keeps that bigger height and can ride well past the yard's back line.
      this.riderWalker.setSpriteHeight(rh);
    }
    bikeSession.dir8 = this.rider.facing8;
    const top = this.riderWalker.topSpeed;
    bikeSession.speedShare = top > 0 ? this.riderWalker.speed / top : 0;
    bikeSession.rideDir = this.riderWalker.rideDir;
    const dt = deltaMs / 1000;

    // Drain grease (smooth, local) while the bike is moving, coasting too. It's persisted to the
    // server on dismount / scene change — no mid-ride syncs, so nothing races with park.
    if (this.riderWalker.speed > 0 && bikeSession.greaseLocal > 0) {
      const burn = BIKE.GREASE_DRAIN_PER_SEC * dt;
      bikeSession.greaseLocal = Math.max(0, bikeSession.greaseLocal - burn);
      this._pendingDrain += burn;
    }
  }

  _flushDrain() {
    if (this._pendingDrain <= 0) return;
    const amount = this._pendingDrain;
    const id = bikeSession.ridingId;
    this._pendingDrain = 0;
    gameState.consumeBikeGrease(amount, id).catch(() => {});  // fire-and-forget; server clamps
  }

  // Mobile action button: "rower" to mount when near the nearest parked owned bike, or
  // to dismount while riding. Mirrors _onSpace; tapping the sprites still works too.
  syncBikeAction() {
    if (isInputLocked(this.scene)) {
      const a = virtualInput.getAction();
      if (a && a.label === 'rower') virtualInput.clearAction();
      return;
    }
    if (this.isRiding) {
      virtualInput.registerAction('rower', () => this.dismount());
      return;
    }
    const id = (!bikeSession.riding && !this._mounting && this._canRide()) ? this._nearestMountable() : null;
    if (id) {
      virtualInput.registerAction('rower', () => this.mount(id));
    } else {
      // Only clear if WE own the current action label (avoid stomping garden's "zbierz").
      const a = virtualInput.getAction();
      if (a && a.label === 'rower') virtualInput.clearAction();
    }
  }

  _onShutdown() {
    this.scene.input.keyboard.off('keydown-SPACE', this._spaceHandler);
    while (this._mountLocks > 0) this._mountUnlock();
    this._mounting = false;
    this._flushDrain();  // persist unsynced grease even if we leave mid-ride
    const a = virtualInput.getAction();
    if (a && a.label === 'rower') virtualInput.clearAction();
    this._releaseArt();
  }

  // Leaving the scene drops the riding atlases the next one may not need — peers' riders and clothes, designs nobody
  // here owns. Kept: our own bikes, and the rider while still riding (the next scene resumes the ride at once).
  // Peers in the next scene load theirs again on demand.
  _releaseArt() {
    if (!this.rig) return;
    releaseBikeArt(this.scene, ownBikeArtKeep({ rig: this.rig, bikes: gameState.raw?.bikes, char: this._char(),
      equipped: gameState.raw?.inventory?.equipped, riding: bikeSession.riding, ridingId: bikeSession.ridingId, look: window.__myLook ?? null }));
  }
}
